<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Button } from '@brighterly/lib-lumee-ui'

import { useTracksLiveKit } from '../../model/tracks-live-kit.hook'
import ScreenShareTile from './ScreenShareTile.vue'
import LocalSharePlaceholder from './LocalSharePlaceholder.vue'

interface StageSlide {
  key: string
  type: 'local' | 'remote' | 'nearpod'
}

defineOptions({
  name: 'ScreenShareStage',
})

const props = withDefaults(defineProps<{
  nearpodCode?: string
}>(), {
  nearpodCode: '',
})

const { remoteScreenTrack, isLocalScreenSharing } = useTracksLiveKit()

const currentIndex = ref<number>(0)

const slides = computed<StageSlide[]>(() => {
  const list: StageSlide[] = []
  if (props.nearpodCode) list.push({ key: 'nearpod', type: 'nearpod' })
  if (isLocalScreenSharing.value) list.push({ key: 'local', type: 'local' })
  if (remoteScreenTrack.value) list.push({ key: 'remote', type: 'remote' })

  return list
})

const hasControls = computed<boolean>(() => slides.value.length > 1)

const setPrevSlide = (): void => {
  if (currentIndex.value > 0) currentIndex.value--
}

const setNextSlide = (): void => {
  if (currentIndex.value < slides.value.length - 1) currentIndex.value++
}

watch(() => slides.value.length, (length) => {
  if (currentIndex.value >= length) {
    currentIndex.value = Math.max(0, length - 1)
  }
})
</script>

<template>
  <div class="screen-share-stage">
    <div
      v-for="(slide, index) in slides"
      v-show="index === currentIndex"
      :key="slide.key"
      class="screen-share-stage__slide"
    >
      <slot
        v-if="slide.type === 'nearpod'"
        name="nearpod"
      />
      <LocalSharePlaceholder v-else-if="slide.type === 'local'" />
      <ScreenShareTile v-else-if="slide.type === 'remote'" />
    </div>

    <div
      v-if="hasControls"
      class="screen-share-stage__controls"
    >
      <span class="screen-share-stage__nav">
        <Button
          view="tertiary"
          size="sm"
          icon-only
          trailing-icon="chevron-left"
          aria-label="Previous screen share"
          :disabled="currentIndex === 0"
          @click="setPrevSlide"
        />
      </span>
      <button
        v-for="(slide, index) in slides"
        :key="slide.key"
        type="button"
        :class="['screen-share-stage__dot', { 'screen-share-stage__dot--active': currentIndex === index }]"
        :aria-label="`Show screen share ${index + 1} of ${slides.length}`"
        :aria-current="currentIndex === index ? 'true' : undefined"
        @click="currentIndex = index"
      />
      <span class="screen-share-stage__nav">
        <Button
          view="tertiary"
          size="sm"
          icon-only
          trailing-icon="chevron-right"
          aria-label="Next screen share"
          :disabled="currentIndex === slides.length - 1"
          @click="setNextSlide"
        />
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./ScreenShareStage.scss" />
